/**
 * ClientVideoCoachingCard Component
 *
 * Video coaching section for client detail page.
 * Shows video comparisons and annotations for form feedback.
 */

import { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useVideoAnnotations, type VideoAnnotation } from "@/hooks/use-video-annotations";
import { useCoachVideoLibrary } from "@/hooks/use-coach-video-library";
import {
  VideoAnnotationCanvas,
  VideoAnnotationToolbar,
  VideoCompareView,
  VideoLibraryPicker,
  ScreenRecorder,
  type AnnotationTool,
  type AnnotationData,
  type VideoAnnotationCanvasRef,
} from "@/components/video-coaching";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import {
  Video,
  Plus,
  MoreVertical,
  Pencil,
  Trash2,
  Play,
  Columns,
  MonitorPlay,
  Loader2,
  FileVideo,
} from "lucide-react";
import { format } from "date-fns";

interface ClientVideoCoachingCardProps {
  clientId: string;
  className?: string;
}

export function ClientVideoCoachingCard({
  clientId,
  className,
}: ClientVideoCoachingCardProps) {
  const canvasRef = useRef<VideoAnnotationCanvasRef>(null);

  const {
    annotations,
    loading,
    fetchAnnotations,
    createAnnotation,
    updateAnnotation,
    deleteAnnotation,
  } = useVideoAnnotations();

  const { getVideoUrl, uploadVideo, createVideo } = useCoachVideoLibrary();

  // Dialog states
  const [showAnnotationDialog, setShowAnnotationDialog] = useState(false);
  const [showCompareDialog, setShowCompareDialog] = useState(false);
  const [showLibraryPicker, setShowLibraryPicker] = useState(false);
  const [showScreenRecorder, setShowScreenRecorder] = useState(false);

  // Annotation state
  const [annotationTitle, setAnnotationTitle] = useState("");
  const [selectedVideoUrl, setSelectedVideoUrl] = useState<string | null>(null);
  const [currentTool, setCurrentTool] = useState<AnnotationTool>("pen");
  const [currentColor, setCurrentColor] = useState("#ef4444");
  const [strokeWidth, setStrokeWidth] = useState(3);
  const [currentAnnotations, setCurrentAnnotations] = useState<AnnotationData>({
    strokes: [],
    shapes: [],
    textLabels: [],
    version: 1,
  });

  // Compare state
  const [leftVideoUrl, setLeftVideoUrl] = useState<string>("");
  const [rightVideoUrl, setRightVideoUrl] = useState<string>("");
  const [selectingFor, setSelectingFor] = useState<"left" | "right" | null>(null);

  // Edit state
  const [editingAnnotation, setEditingAnnotation] = useState<VideoAnnotation | null>(null);

  // Saving state
  const [isSaving, setIsSaving] = useState(false);

  // Fetch annotations for this client on mount
  useState(() => {
    fetchAnnotations(clientId);
  });

  // Handle starting new annotation from video URL
  const handleStartAnnotation = async (videoUrl: string) => {
    setSelectedVideoUrl(videoUrl);
    setAnnotationTitle("");
    setCurrentAnnotations({
      strokes: [],
      shapes: [],
      textLabels: [],
      version: 1,
    });
    setShowAnnotationDialog(true);
  };

  // Handle video selection from library
  const handleVideoSelect = async (video: any) => {
    const url = await getVideoUrl(video.storage_path);
    if (url) {
      if (selectingFor === "left") {
        setLeftVideoUrl(url);
      } else if (selectingFor === "right") {
        setRightVideoUrl(url);
      } else {
        handleStartAnnotation(url);
      }
    }
    setShowLibraryPicker(false);
    setSelectingFor(null);
  };

  // Handle screen recording complete
  const handleRecordingComplete = async (blob: Blob) => {
    setShowScreenRecorder(false);

    // Convert blob to file and upload
    const file = new File([blob], `screen-recording-${Date.now()}.webm`, {
      type: "video/webm",
    });

    toast.loading("Uploading screen recording...");

    const result = await uploadVideo(file);
    if (result) {
      await createVideo({
        title: `Screen Recording - ${format(new Date(), "MMM d, yyyy")}`,
        category: "form_feedback",
        storage_path: result.storagePath,
        file_size_bytes: file.size,
        is_public: false,
      });

      const url = await getVideoUrl(result.storagePath);
      if (url) {
        handleStartAnnotation(url);
      }
    }

    toast.dismiss();
  };

  // Save annotation
  const handleSaveAnnotation = async () => {
    if (!selectedVideoUrl) return;

    setIsSaving(true);

    try {
      const annotationData = canvasRef.current?.getAnnotations() || currentAnnotations;

      if (editingAnnotation) {
        await updateAnnotation(editingAnnotation.id, {
          title: annotationTitle,
          annotation_data: annotationData,
        });
      } else {
        await createAnnotation({
          client_id: clientId,
          source_video_url: selectedVideoUrl,
          title: annotationTitle,
          annotation_data: annotationData,
        });
      }

      setShowAnnotationDialog(false);
      setEditingAnnotation(null);
      fetchAnnotations(clientId);
    } finally {
      setIsSaving(false);
    }
  };

  // Open edit annotation
  const handleEditAnnotation = async (annotation: VideoAnnotation) => {
    setEditingAnnotation(annotation);
    setAnnotationTitle(annotation.title || "");
    setSelectedVideoUrl(annotation.source_video_url);
    setCurrentAnnotations(annotation.annotation_data);
    setShowAnnotationDialog(true);
  };

  // Delete annotation
  const handleDeleteAnnotation = async (id: string) => {
    await deleteAnnotation(id);
    fetchAnnotations(clientId);
  };

  // Start comparison
  const handleStartComparison = () => {
    setLeftVideoUrl("");
    setRightVideoUrl("");
    setShowCompareDialog(true);
  };

  return (
    <Card className={className}>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between">
          <div>
            <CardTitle className="flex items-center gap-2">
              <Video className="h-5 w-5" />
              Video Coaching
            </CardTitle>
            <CardDescription>
              Form feedback, video comparisons, and annotations
            </CardDescription>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="gap-2">
                <Plus className="h-4 w-4" />
                New
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => setShowLibraryPicker(true)}>
                <FileVideo className="h-4 w-4 mr-2" />
                Annotate Library Video
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setShowScreenRecorder(true)}>
                <MonitorPlay className="h-4 w-4 mr-2" />
                Record Screen
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={handleStartComparison}>
                <Columns className="h-4 w-4 mr-2" />
                Compare Videos
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </CardHeader>

      <CardContent>
        {loading ? (
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : annotations.length === 0 ? (
          <div className="text-center py-8">
            <Video className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
            <p className="text-muted-foreground">No video annotations yet</p>
            <p className="text-sm text-muted-foreground mb-4">
              Create annotated videos to provide form feedback
            </p>
          </div>
        ) : (
          <div className="space-y-3">
            {annotations.slice(0, 5).map((annotation) => (
              <div
                key={annotation.id}
                className="flex items-center gap-3 p-3 border rounded-lg hover:bg-muted/50 transition-colors"
              >
                <div className="w-16 h-10 bg-muted rounded flex items-center justify-center">
                  <Video className="h-5 w-5 text-muted-foreground" />
                </div>

                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">
                    {annotation.title || "Untitled Annotation"}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {format(new Date(annotation.created_at), "MMM d, yyyy")}
                  </p>
                </div>

                <Badge variant="outline" className="shrink-0">
                  {(annotation.annotation_data?.strokes?.length || 0) +
                    (annotation.annotation_data?.shapes?.length || 0)}{" "}
                  marks
                </Badge>

                <DropdownMenu>
                  <DropdownMenuTrigger asChild>
                    <Button variant="ghost" size="icon" className="shrink-0">
                      <MoreVertical className="h-4 w-4" />
                    </Button>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent align="end">
                    <DropdownMenuItem onClick={() => handleEditAnnotation(annotation)}>
                      <Pencil className="h-4 w-4 mr-2" />
                      Edit
                    </DropdownMenuItem>
                    <DropdownMenuSeparator />
                    <DropdownMenuItem
                      className="text-destructive"
                      onClick={() => handleDeleteAnnotation(annotation.id)}
                    >
                      <Trash2 className="h-4 w-4 mr-2" />
                      Delete
                    </DropdownMenuItem>
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
            ))}

            {annotations.length > 5 && (
              <p className="text-sm text-center text-muted-foreground pt-2">
                +{annotations.length - 5} more annotations
              </p>
            )}
          </div>
        )}
      </CardContent>

      {/* Annotation Dialog */}
      <Dialog open={showAnnotationDialog} onOpenChange={setShowAnnotationDialog}>
        <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>
              {editingAnnotation ? "Edit Annotation" : "Create Annotation"}
            </DialogTitle>
            <DialogDescription>
              Draw on the video to highlight form corrections
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="annotation-title">Title</Label>
              <Input
                id="annotation-title"
                value={annotationTitle}
                onChange={(e) => setAnnotationTitle(e.target.value)}
                placeholder="e.g., Squat Form Correction"
              />
            </div>

            <VideoAnnotationToolbar
              currentTool={currentTool}
              onToolChange={setCurrentTool}
              currentColor={currentColor}
              onColorChange={setCurrentColor}
              strokeWidth={strokeWidth}
              onStrokeWidthChange={setStrokeWidth}
              onUndo={() => canvasRef.current?.undo()}
              onRedo={() => canvasRef.current?.redo()}
              onClear={() => canvasRef.current?.clear()}
            />

            {selectedVideoUrl && (
              <VideoAnnotationCanvas
                ref={canvasRef}
                videoUrl={selectedVideoUrl}
                width={800}
                height={450}
                currentTool={currentTool}
                currentColor={currentColor}
                strokeWidth={strokeWidth}
                initialAnnotations={currentAnnotations}
                onAnnotationsChange={setCurrentAnnotations}
                showVideo={true}
              />
            )}

            <div className="flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowAnnotationDialog(false);
                  setEditingAnnotation(null);
                }}
              >
                Cancel
              </Button>
              <Button onClick={handleSaveAnnotation} disabled={isSaving}>
                {isSaving ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  "Save Annotation"
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Comparison Dialog */}
      <Dialog open={showCompareDialog} onOpenChange={setShowCompareDialog}>
        <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Video Comparison</DialogTitle>
            <DialogDescription>
              Compare reference form with client's form side-by-side
            </DialogDescription>
          </DialogHeader>

          {(!leftVideoUrl || !rightVideoUrl) ? (
            <div className="grid grid-cols-2 gap-4 py-8">
              <Card
                className={cn(
                  "p-6 cursor-pointer hover:bg-muted/50 transition-colors",
                  leftVideoUrl && "border-success"
                )}
                onClick={() => {
                  setSelectingFor("left");
                  setShowLibraryPicker(true);
                }}
              >
                <div className="text-center space-y-2">
                  <FileVideo className="h-8 w-8 mx-auto text-muted-foreground" />
                  <p className="font-medium">Reference Video</p>
                  <p className="text-sm text-muted-foreground">
                    {leftVideoUrl ? "Video selected" : "Click to select"}
                  </p>
                </div>
              </Card>

              <Card
                className={cn(
                  "p-6 cursor-pointer hover:bg-muted/50 transition-colors",
                  rightVideoUrl && "border-success"
                )}
                onClick={() => {
                  setSelectingFor("right");
                  setShowLibraryPicker(true);
                }}
              >
                <div className="text-center space-y-2">
                  <FileVideo className="h-8 w-8 mx-auto text-muted-foreground" />
                  <p className="font-medium">Client Video</p>
                  <p className="text-sm text-muted-foreground">
                    {rightVideoUrl ? "Video selected" : "Click to select"}
                  </p>
                </div>
              </Card>
            </div>
          ) : (
            <VideoCompareView
              leftVideoUrl={leftVideoUrl}
              rightVideoUrl={rightVideoUrl}
              leftLabel="Reference"
              rightLabel="Client Form"
            />
          )}
        </DialogContent>
      </Dialog>

      {/* Video Library Picker */}
      <VideoLibraryPicker
        open={showLibraryPicker}
        onOpenChange={setShowLibraryPicker}
        onSelect={handleVideoSelect}
        title={selectingFor ? `Select ${selectingFor === "left" ? "Reference" : "Client"} Video` : "Select Video"}
      />

      {/* Screen Recorder */}
      <ScreenRecorder
        open={showScreenRecorder}
        onOpenChange={setShowScreenRecorder}
        onRecordingComplete={handleRecordingComplete}
      />
    </Card>
  );
}

export default ClientVideoCoachingCard;
